"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateRangePicker = exports.DatePickerDialog = exports.DatePicker = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../../theme");
/**
 * A date picker component that allows users to select a single date.
 * Combines a text input with a calendar popover for date selection.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/DatePicker.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-datepicker--default}
 *
 * @example
 * ```tsx
 * <DatePicker>
 *   <Label>Date</Label>
 *   <InputGroup>
 *     <UnstyledDateInput>
 *       {(segment) => <DateSegment segment={segment} />}
 *     </UnstyledDateInput>
 *     <IconButton size="small">
 *       <ArrowDropDownIcon size={16} />
 *     </IconButton>
 *   </InputGroup>
 *   <HelperText slot="description">Description</HelperText>
 *   <FieldError />
 *   <RACPopover>
 *     <DatePickerDialog>
 *       <Calendar aria-label="Appointment date">
 *         <CalendarHeader>
 *           <IconButton slot="previous"><ArrowLeftIcon /></IconButton>
 *           <CalendarHeading />
 *           <IconButton slot="next"><ArrowRightIcon /></IconButton>
 *         </CalendarHeader>
 *         <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
 *       </Calendar>
 *     </DatePickerDialog>
 *   </RACPopover>
 * </DatePicker>
 * ```
 *
 * @param value - The current date value. Defaults to null
 * @param defaultValue - The default date value. Defaults to null
 * @param isDisabled - Whether the date picker is disabled. Defaults to false
 * @param isReadOnly - Whether the date picker is read only. Defaults to false
 * @param isRequired - Whether the date picker is required. Defaults to false
 * @param minValue - The minimum allowed date value. Defaults to null
 * @param maxValue - The maximum allowed date value. Defaults to null
 * @param onChange - Handler that is called when the date changes
 * @param children - The label, input group, helper text, error, and popover components to display
 */
exports.DatePicker = (0, styled_1.default)(react_aria_components_1.DatePicker)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"])), theme_1.theme.space.space8);
/**
 * A dialog component that contains the calendar popover for date selection.
 * Must be used within a RACPopover component and contains the Calendar component.
 *
 * @example
 * ```tsx
 * <DatePickerDialog>
 *   <Calendar aria-label="Appointment date">
 *     <CalendarHeader>
 *       <IconButton slot="previous"><ArrowLeftIcon /></IconButton>
 *       <CalendarHeading />
 *       <IconButton slot="next"><ArrowRightIcon /></IconButton>
 *     </CalendarHeader>
 *     <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
 *   </Calendar>
 * </DatePickerDialog>
 * ```
 *
 * @param children - The calendar component to display
 */
exports.DatePickerDialog = (0, styled_1.default)(react_aria_components_1.Dialog)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  max-height: inherit;\n  overflow: auto;\n  padding: ", ";\n  background-color: ", ";\n  gap: ", ";\n  outline: none;\n  border: ", " solid ", ";\n  border-radius: ", ";\n"], ["\n  max-height: inherit;\n  overflow: auto;\n  padding: ", ";\n  background-color: ", ";\n  gap: ", ";\n  outline: none;\n  border: ", " solid ", ";\n  border-radius: ", ";\n"])), theme_1.theme.space.space16, theme_1.theme.colors.surface200, theme_1.theme.space.space2, theme_1.theme.borderWidths.regular, theme_1.theme.colors.n80, theme_1.theme.radii.card);
/**
 * A date range picker component that allows users to select a date range.
 * Combines two text inputs with a calendar popover for range selection.
 *
 * @example
 * ```tsx
 * <DateRangePicker>
 *   <Label>Date</Label>
 *   <InputGroup>
 *     <UnstyledDateInput slot="start">
 *       {(segment) => <DateSegment segment={segment} />}
 *     </UnstyledDateInput>
 *     <span aria-hidden="true">–</span>
 *     <UnstyledDateInput slot="end">
 *       {(segment) => <DateSegment segment={segment} />}
 *     </UnstyledDateInput>
 *     <IconButton size="small">
 *       <ArrowDropDownIcon size={16} />
 *     </IconButton>
 *   </InputGroup>
 *   <HelperText slot="description">Description</HelperText>
 *   <FieldError />
 *   <RACPopover>
 *     <DatePickerDialog>
 *       <RangeCalendar aria-label="Appointment date">
 *         <CalendarHeader>
 *           <IconButton slot="previous"><ArrowLeftIcon /></IconButton>
 *           <CalendarHeading />
 *           <IconButton slot="next"><ArrowRightIcon /></IconButton>
 *         </CalendarHeader>
 *         <CalendarGrid>{(date) => <RangeCalendarCell date={date} />}</CalendarGrid>
 *       </RangeCalendar>
 *     </DatePickerDialog>
 *   </RACPopover>
 * </DateRangePicker>
 * ```
 *
 * @param value - The current date range value. Defaults to null
 * @param defaultValue - The default date range value. Defaults to null
 * @param isDisabled - Whether the date range picker is disabled. Defaults to false
 * @param isReadOnly - Whether the date range picker is read only. Defaults to false
 * @param isRequired - Whether the date range picker is required. Defaults to false
 * @param minValue - The minimum allowed date value. Defaults to null
 * @param maxValue - The maximum allowed date value. Defaults to null
 * @param onChange - Handler that is called when the date range changes
 * @param children - The label, input group, helper text, error, and popover components to display
 */
exports.DateRangePicker = (0, styled_1.default)(react_aria_components_1.DateRangePicker)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n\n  &[slot='start'] {\n    flex: 0;\n  }\n  &[slot='start'] + span {\n    padding: 0 ", ";\n    flex: 0;\n  }\n\n  &[slot='end'] {\n    margin-right: ", ";\n    flex: 1;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n\n  &[slot='start'] {\n    flex: 0;\n  }\n  &[slot='start'] + span {\n    padding: 0 ", ";\n    flex: 0;\n  }\n\n  &[slot='end'] {\n    margin-right: ", ";\n    flex: 1;\n  }\n"])), theme_1.theme.space.space8, theme_1.theme.space.space4, theme_1.theme.space.space32);
var templateObject_1, templateObject_2, templateObject_3;
//# sourceMappingURL=DatePicker.js.map